import { useCallback, useEffect, useState } from 'react';
import localApiAdapter from '../../../core/services/localApiAdapter';
import {
  DASHBOARD_ENDPOINT_FALLBACKS,
  fetchSyntheticDashboard,
  normalizeDashboardPayload,
} from './dashboardPayloadNormalize';

async function loadDashboardPayload(endpoint, apiQueryParams = null) {
  const candidates = [endpoint, ...(DASHBOARD_ENDPOINT_FALLBACKS[endpoint] || [])];
  let lastError = null;
  const queryOpts = apiQueryParams && Object.keys(apiQueryParams).length
    ? { params: apiQueryParams }
    : undefined;

  for (const candidate of candidates) {
    try {
      const response = await localApiAdapter.get(candidate, queryOpts);
      return normalizeDashboardPayload(candidate, response);
    } catch (err) {
      lastError = err;
    }
  }

  const synthetic = await fetchSyntheticDashboard(localApiAdapter, endpoint);
  if (synthetic) {
    return synthetic;
  }

  throw lastError || new Error('Failed to load dashboard');
}

export default function useModuleDashboard(endpoint, apiQueryParams = null, refreshEvents = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const queryKey = JSON.stringify(apiQueryParams || {});
  const refreshKey = JSON.stringify(refreshEvents || []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = queryKey ? JSON.parse(queryKey) : null;
      const payload = await loadDashboardPayload(endpoint, params);
      setData(payload);
    } catch (err) {
      console.error(`Dashboard load failed (${endpoint}):`, err);
      setError(err?.message || 'Failed to load dashboard');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [endpoint, queryKey]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const events = refreshKey ? JSON.parse(refreshKey) : [];
    if (!events.length) return undefined;
    const handler = () => { void load(); };
    events.forEach((ev) => window.addEventListener(ev, handler));
    return () => events.forEach((ev) => window.removeEventListener(ev, handler));
  }, [load, refreshKey]);

  return { data, loading, error, refresh: load };
}
